import type { VibeCheckAttachment } from '../api';
import { clipboardImageError } from './clipboardImage';

// Keep these limits aligned with server/src/vibeCheckAttachments.ts.
export const MAX_CONVERSATION_ATTACHMENTS = 8;
export const MAX_CONVERSATION_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.md,.csv,.json,.html,.htm';
const textExtensions: Record<string, VibeCheckAttachment['mediaType']> = {
  txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', json: 'application/json', html: 'text/html', htm: 'text/html',
};
const binaryExtensions: Record<string, VibeCheckAttachment['mediaType']> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf',
};

export function attachmentBytes(attachment: VibeCheckAttachment) {
  return attachment.data.length * 3 / 4 - (attachment.data.endsWith('==') ? 2 : attachment.data.endsWith('=') ? 1 : 0);
}
export function attachmentSelectionError(attachments: VibeCheckAttachment[], previous: VibeCheckAttachment[]) {
  if (attachments.length > 4) return 'Attach up to 4 files at a time.';
  if (attachments.length + previous.length > MAX_CONVERSATION_ATTACHMENTS) return 'Use up to 8 files per conversation. Start a new conversation to add more.';
  const all = [...attachments, ...previous];
  if (all.reduce((sum, attachment) => sum + attachmentBytes(attachment), 0) > MAX_CONVERSATION_ATTACHMENT_BYTES) return 'Keep attachments under 10 MB total per conversation.';
  const textBytes = all.filter(attachment => !attachment.mediaType.startsWith('image/') && attachment.mediaType !== 'application/pdf').reduce((sum, attachment) => sum + attachmentBytes(attachment), 0);
  if (textBytes > 200 * 1024) return 'Keep text attachments under 200 KB total per conversation.';
  return null;
}

export async function readAttachment(file: File): Promise<VibeCheckAttachment> {
  const extension = file.name.split('.').at(-1)?.toLowerCase() ?? '';
  const mediaType = Object.hasOwn(textExtensions, extension) ? textExtensions[extension]
    : Object.hasOwn(binaryExtensions, extension) ? binaryExtensions[extension] : undefined;
  if (!mediaType) throw new Error('Use images, PDFs, or TXT, Markdown, CSV, JSON and HTML files.');
  if (!file.name.trim() || file.name.length > 160 || /[\x00-\x1f/\\]/.test(file.name)) throw new Error('Use a shorter file name without path separators.');
  if (!file.size) throw new Error('This file is empty.');
  if (mediaType.startsWith('image/')) {
    const error = clipboardImageError(new Blob([file], { type: mediaType }));
    if (error) throw new Error(error);
  } else if (file.size > (mediaType === 'application/pdf' ? 5 * 1024 * 1024 : 100 * 1024)) {
    throw new Error(mediaType === 'application/pdf' ? 'PDFs must not exceed 5 MB.' : 'Text and data files must not exceed 100 KB.');
  }
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Could not read the attachment. Try selecting it again.'));
    reader.onabort = () => reject(new Error('Attachment reading was cancelled.'));
    reader.readAsDataURL(file);
  });
  return { name: file.name, mediaType, data };
}
